import { Button, Card, Skeleton, StateView } from '@hopex/ui';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../api.ts';

/** Loads `path` (null = skip); `reload` re-fetches. Errors are explicit, never an empty table. */
export function useRemote<T>(path: string | null) {
  const [tick, setTick] = useState(0);
  const key = path === null ? null : `${path}#${String(tick)}`;
  const [state, setState] = useState<{ data: T | null; error: string | null; for: string | null }>({
    data: null,
    error: null,
    for: null,
  });
  useEffect(() => {
    if (!path || !key) return;
    let live = true;
    api.get<T>(path).then(
      (data) => live && setState({ data, error: null, for: key }),
      (e: unknown) => live && setState({ data: null, error: errorMessage(e), for: key }),
    );
    return () => {
      live = false;
    };
  }, [path, key]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  // Previous data stays visible while a new page/filter loads (stable layout, no flash of empty).
  return {
    data: state.data,
    error: state.error,
    loading: key !== null && state.for !== key,
    reload,
  };
}

export function Remote<T>({
  r,
  children,
}: {
  r: { data: T | null; error: string | null; reload: () => void };
  children: (d: T) => ReactNode;
}) {
  if (r.error)
    return (
      <Card className="p-6">
        <StateView
          kind="error"
          title="دریافت اطلاعات ممکن نشد"
          description={r.error}
          action={<Button onPress={r.reload}>تلاش دوباره</Button>}
        />
      </Card>
    );
  if (!r.data)
    return (
      <Card className="flex flex-col gap-3 p-6" aria-busy="true">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </Card>
    );
  return <>{children(r.data)}</>;
}
